/* globals localStorage */

require('../utils').handlebars.register()

const {Hierarchy, getGndId, arrayify} = require('../utils')
const {helpers} = require('../utils/handlebars')
const tpl = require('../tpl')
const {gndHierarchy} = require('../data')

/**
 * Simple Cache for JSON data
 */
class AuthoritiesCache {

    constructor(opts={}) {
        Object.assign(this, {
            endpoint: 'https://digi.ub.uni-heidelberg.de/normdaten',
            storage: localStorage,
            inMemory: {}
        }, opts)
        this.fromStorage()
    }

    fromStorage() {this.inMemory =  JSON.parse(this.storage.getItem('authorities') || '{}')}
    toStorage() {this.storage.setItem('authorities', JSON.stringify(this.inMemory))}

    getSync(url) {
        url = url.match(/^https?:\/\//) ? url : `${this.endpoint}/${url}`
        if (!(url in this.inMemory)) {
            throw new Error(`Cannot get ${url} synchronously, it hasn't been fetched yet :'(`)
        }
        return this.inMemory[url]
    }

    get(url) {
        url = url.match(/^https?:\/\//) ? url : `${this.endpoint}/${url}`
        if (!(url in this.inMemory)) {
            return fetch(url).then(resp => resp.json()).then(fetched => {
                this.inMemory[url] = fetched
                this.toStorage()
                return fetched
            })
        } else {
            return Promise.resolve(this.inMemory[url])
        }
    }

}

module.exports = class UbhdGndPlugin {

    /**
     * ### AuthoritiesClient.plugin('ubhd/gnd', opts)
     *
     * - `@param {object} opts` Options for constructor
     * - `@param {object} opts.endpoint` URL of the authorities-backend server. Default: `http://digi.ub.uni-heidelberg.de/normdaten`
     * - `@param {number} opts.width` Width of the infoBox. Default: 500
     */
    constructor(opts={}) {
        opts = Object.assign({
            templates: tpl,
            endpoint: 'https://digi.ub.uni-heidelberg.de/normdaten',
            width: 500,
            gndHierarchy,
        }, opts)
        this.templates = opts.templates
        this.width = opts.width
        this.endpoint = opts.endpoint
        this.gndHierarchy = new Hierarchy(opts.gndHierarchy)
        this.cache = new AuthoritiesCache({endpoint: this.endpoint})
    }

    // -----------
    // PRIVATE API
    // -----------

    // http://ws.gbv.de/suggest/gn250/
    // http://www.opensearch.org/Specifications/OpenSearch/Extensions/Suggestions/1.1#Example
    _toOpenSearch(searchOrSuggest, term, json) {
        const ret = [term, [], [], []]
        if (searchOrSuggest === 'search') {
            json.response.docs.forEach(doc => {
                ret[1].push(helpers.helpfulPreferredName(doc))
                ret[2].push(doc._type[0])
                ret[3].push(doc.gndIdentifier)
            })
        } else {
            json.suggestions.forEach(suggestion => {
                ret[1].push(suggestion.term)
                ret[2].push(suggestion['@type'])
                ret[3].push(suggestion.payload)
            })
        }
        return ret
    }

    _templateForType(type) {
        let chain = []
        if (Array.isArray(type)) {
            type.forEach(t => chain.push(...this.gndHierarchy.ancestorsOrSelf(t)))
        } else {
            chain.push(...this.gndHierarchy.ancestorsOrSelf(type))
        }
        const tplName = chain.find(c => this.templates[c]) || 'fallback'
        return (entity, opts) => {
            const self = this
            const content = this.templates[tplName]({self, entity, opts})
            return this.templates.layout({self, entity, content, opts})
        }
    }

    // Execute a search or suggest query (they are identical call-wise just
    // different formats returned)
    _searchOrSuggest(searchOrSuggest, term, opts={}) {
        opts.count = opts.count || 10
        let url = `${this.endpoint}/gnd`
        if (searchOrSuggest === 'suggest') url += '/suggest/'
        url += `?q=${encodeURIComponent(term)}&count=${opts.count}`
        if ('queryLevel' in opts) {
            url += `&queryLevel=${encodeURIComponent(opts.queryLevel)}`
        }        
        if (opts.type && opts.withSubTypes) {
            let types = []
            opts.type.forEach(t => types.push(...this.gndHierarchy.descendantsOrSelf(t)))
            opts.type = types
        }
        if (opts.type) url += `&type=${encodeURIComponent(arrayify(opts.type).join(' OR '))}`        
        return new Promise((resolve, reject) => {
            fetch(url)
                .then(response => response.json())
                .then(json => {
                    if (opts.format === 'opensearch') {
                        return resolve(this._toOpenSearch(searchOrSuggest, term, json))
                    } else {
                        return resolve(json)
                    }
                })
                .catch(err => reject(err))
        })
    }

    _entityToInfobox(entity, opts={}) {
        opts.thumbnailWidth = opts.thumbnailWidth || 200        
        opts.heading = ('heading' in opts) ? opts.heading : true
        if (!opts.type) opts.type = entity['@type']
        return this._templateForType(opts.type)(entity, opts)
    }

    // ----------
    // PUBLIC API
    // ----------

    /**
     *
     * ### `infoBox(id)`
     *
     * Promise a Wikipedia-Style Infobox about `id` as HTML.
     *
     * - `@param {string} id`: GND of the entity to infoBox
     * - `@param {object} opts`:
     *   - `@param {string} opts.type`: Type restriction. Format infoBox for this one type.
     *   - `@param {string} opts.format`: Return format. Either `html` (for the formatted HTML) or `json` for both formatted HTML and entity.
     *   - `@param {boolean} opts.showTypes`: Whether to show the GND types info
     *   - `@param {boolean} opts.showThumbnail`: Whether to add the image to the infobox
     *   - `@param {int} opts.thumbnailWidth`: Width of the thumbnail image, which may be available for some entities
     * - `@return {Promise(string)}` A promise that resolves to the formatted HTML
     */
    infoBox(id, opts={}) {
        return Promise.all([
            this.get(id),
            this.cache.get(`${this.endpoint}/gnd-sc`),
            this.cache.get(`${this.endpoint}/gnd-ac`)
        ]).then(([entity]) => {
            const html = this._entityToInfobox(entity, opts)
            if (opts.format === 'json') {
                entity.preferredName = helpers.preferredName(entity)
                return {entity, html}
            } else {
                return html
            }
        })
    }


    /**
     *
     * ### `get(id)`
     *
     * Return all the data about entity with GND id
     *
     */
    get(id) {
        return new Promise((resolve, reject) => {
            fetch(`${this.endpoint}/gnd/${encodeURIComponent(getGndId(id))}?resolveLabels=1`)
                .then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        resolve(response.json())
                    }
                    else {
                        reject(response.statusText)
                    }
                })
                .catch(err => reject(err))
        })
    }


    /**
     *
     * ### `search(term, opts)`
     *
     * - `@param {string} term`: Search term
     * - `@param {object} opts`:
     *   - `@param {number} opts.count`: Number of results to return. Default: 10
     *   - `@param {string} opts.withSubTypes`: Also include subtypes of entities in search
     *   - `@param {string} opts.type`: Type restrictions. Results must be of one of these types.
     *     space-separated string. Default: no restriction
     *   - `@param {string} opts.format`: Can be `opensearch` to return
     *     results in a [opensearch-like structure](http://www.opensearch.org/Specifications/OpenSearch/Extensions/Suggestions/1.1#Example)
     */
    search(...args) {return this._searchOrSuggest('search', ...args)}

    /**
     *
     * ### `suggest(term, opts)`
     *
     * Much faster than search but less information per result.
     *
     * * `opts`: See [search](#search-term-opts)
     *
     */
    suggest(...args) {return this._searchOrSuggest('suggest', ...args)}

}

// vim: sw=4 ts=4
